<template>
  <BlocksCard v-if="development" class="development-developer">
    <h2 class="__heading __underlined">
      Developer
    </h2>
    <div class="__developer-logo-wrapper">
      <div class="__development-logo-wrapper">
        <img v-lazy-load  :data-src="development.logoUrl" alt="Development logo">
      </div>
    </div>

    <h3 v-if="development.developerInfo" class="__developer-name">
      {{ development.developerInfo }}
    </h3>

    <div class="d-flex justify-content-center flex-column align-items-center">
      <ElementsButtonsRegular text size="md" label="Email Developer" :append-icon="emailIcon" @click="isEnquiryOpen = true" />
      <ElementsButtonsRegular text size="md" label="Chat with Developer" :append-icon="messageIcon" :loading="!!isChatInitiating" @click="triggerInitiateChat" />
    </div>

    <!-- Action buttons -->
    <div class="__action-button">
      <ElementsButtonsRegular v-if="development.website" secondary label="Development Website" size="sm" :button-link="development.website" link-target="_blank" :analytics-click-event-data="{entity: 'Development', buttonAction: `view ${development.slug} website`, target: '_blank', displayedLocation: 'development view'}" />
      <ElementsButtonsRegular v-if="development.developerWebsite" secondary label="Developer Profile" size="sm" :button-link="development.developerWebsite" link-target="_blank" :analytics-click-event-data="{entity: 'Development', buttonAction: `view ${development.slug} developer profile`, target: '_blank', displayedLocation: 'development view'}" />
    </div>

    <!-- Email Enquiry -->
    <CommonEmailEnquiry v-model:is-open="isEnquiryOpen" enquiry-type="development" :enquiring-entity-id="development.did" :tracking-event-name="development.name" />
  </BlocksCard>
</template>

<script setup lang="ts">
import {Development} from '~/@types/development';
import emailIcon from '~/assets/images/icons/email.png';
import messageIcon from '~/assets/images/icons/message.webp';
import {useAuthUserStore} from '~/store/authUser';
import {useLayoutStore} from '~/store/layout';

// PROPS Definitions
//----------------------------------------------------------------------------------------------------------------------
const props = defineProps({
  development: {
    type: [Object, null] as PropType<Development | null>,
    required: true,
  },
});

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const isChatInitiating: Ref<boolean> = ref(false);
const {$initiateChat, $metaPixelTrackEvent}: any = useNuxtApp();
const authUserStore = useAuthUserStore();
const router = useRouter();
const layoutStore = useLayoutStore();
const isEnquiryOpen: Ref<boolean> = ref(false);


/**
 * Trigger initiate chat
 */
async function triggerInitiateChat () {
  if (!props.development) return;
  triggerPixelLeadEvent('chat');

  isChatInitiating.value = true;

  authUserStore.setChatInitiationData({
    location: 'development',
    locationDid: props.development.did,
    url: window.location.href,
    roomId: null,
  });

  if (authUserStore.user) {
    const response = await $initiateChat('development', props.development.did);
    if (response) {
      authUserStore.setChatInitiationData({
        location: 'development',
        locationDid: props.development.did,
        url: window.location.href,
        roomId: response.code === 200 ? response.data.toString() : null,
        hasError: response.code !== 200,
      });
    }

    setTimeout(() => {
      layoutStore.setChatOpen(true);
      isChatInitiating.value = false;
    }, 1500);
  } else {
    router.push('/auth/login');
    isChatInitiating.value = false;
  }
}

/**
 * Trigger pixel event
 */
function triggerPixelLeadEvent (type: 'phone' | 'chat') {
  if (!props.development) return;
  // Track meta event
  $metaPixelTrackEvent('Lead', {
    content_name: props.development.name,
    content_category: props.development.type ?? 'Development',
    content_ids: [props.development.did],
    content_type: 'Development',
    lead_type: type,
  });
}
</script>

<style lang="scss">
.development-developer {
  .__developer-logo-wrapper {
    margin-top: 12px;
    margin-bottom: 12px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .__development-logo-wrapper {
      width: 185px;
      height: 185px;
      border-radius: 50%;
      overflow: hidden;
      background: #fff;
      border: 1px solid #e0e0e0;
      padding: 12px;
      display: flex;
      justify-content: center;
      align-items: center;

      img {
        max-width: 100%;
        max-height: 100%;
      }
    }
  }

  .__developer-name {
    text-align: center;
    margin-bottom: 24px;
  }
}
</style>
